import React, { useState } from 'react';
import { IcoGift, IcoParty, IcoPin, IcoShop, IcoTruck } from '../common/Icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// One-time welcome popup shown after admin approval.
export function WelcomePopup() {
  const { user, dismissWelcome } = useAuth();
  if (!user || user.approval_status !== 'approved' || !user.welcome_pending) return null;
  return (
    <div style={g.overlay} onMouseDown={e => { if (e.target === e.currentTarget) dismissWelcome(); }}>
      {/* The overlay and modal styles both animate, but the keyframes live in a component this one
          never rendered — so they resolved to nothing and the popup appeared without its entrance. */}
      <GuideKeyframes />
      <div style={g.modal}>
        <div style={{ color: '#16a34a' }}><IcoParty size={44} /></div>
        <h2 style={g.wTitle}>Welcome to ModCommerce{user.name ? `, ${user.name.split(' ')[0]}` : ''}!</h2>
        <p style={g.wSub}>Your account is approved. Let's get you shipping — just finish the 3 quick setup steps: add a sales channel, a location, and a courier.</p>
        <button style={g.wBtn} onClick={dismissWelcome}>Let's set up →</button>
      </div>
    </div>
  );
}

// "Recharge ₹1000 → 500 free" reminder.
// Dismiss = hidden for the rest of that day; shows again next day.
// After 5 total dismissals it never shows again for that user.
const RC_MAX_DISMISS = 5;
const todayStr = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD (local day)

function readDismiss(uid) {
  try { return JSON.parse(localStorage.getItem(`mc_rc_dismiss_${uid}`)) || { date: '', count: 0 }; }
  catch { return { date: '', count: 0 }; }
}

export function RechargeReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const uid = user?.id || user?.userId || user?.email || 'anon';
  const [state, setState] = useState(() => readDismiss(uid));

  // Re-read when the account changes (e.g. after login).
  React.useEffect(() => { setState(readDismiss(uid)); }, [uid]);

  const dismiss = () => {
    const next = { date: todayStr(), count: (state.count || 0) + 1 };
    try { localStorage.setItem(`mc_rc_dismiss_${uid}`, JSON.stringify(next)); } catch {}
    setState(next);
  };

  if (!user || user.approval_status !== 'approved' || user.has_recharged) return null;
  if (state.count >= RC_MAX_DISMISS) return null;        // dismissed 5+ times → gone for good
  if (state.date === todayStr()) return null;            // already closed today → back tomorrow
  return (
    <div style={g.remBar}>
      <span style={g.remText}><IcoGift size={14} /> <b>New-user offer:</b> Recharge ₹1000 and get <b>500 credits free</b>.</span>
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={g.remBtn} onClick={() => navigate('/dashboard/billing')}>Recharge now</button>
        <button style={g.remClose} onClick={dismiss} title="Dismiss">✕</button>
      </div>
    </div>
  );
}

// Guided setup — a floating progress pill (bottom-right) that opens an animated 3-step modal.
// Non-blocking; shipping itself is guarded server-side. Auto-opens once per browser, then on demand.
const STEPS = [
  { key: 'store_done', Ico: IcoShop, label: 'Add a sales channel', desc: 'Connect the store where your orders come from.', to: '/dashboard/stores' },
  { key: 'location_done', Ico: IcoPin, label: 'Add a location', desc: 'Set the warehouse you ship your orders from.', to: '/dashboard/locations' },
  { key: 'courier_done', Ico: IcoTruck, label: 'Add a courier', desc: 'Connect a courier so you can book shipments.', to: '/dashboard/couriers' },
];

// Keyframes injected once (inline styles can't hold @keyframes).
function GuideKeyframes() {
  return (
    <style>{`
      @keyframes mcPillPulse { 0%,100% { box-shadow: 0 8px 22px rgba(37,99,235,0.35), 0 0 0 0 rgba(37,99,235,0.45); } 50% { box-shadow: 0 8px 22px rgba(37,99,235,0.35), 0 0 0 10px rgba(37,99,235,0); } }
      @keyframes mcModalIn { from { opacity: 0; transform: translateY(14px) scale(0.98); } to { opacity: 1; transform: none; } }
      @keyframes mcOverlayIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes mcTickPop { 0% { transform: scale(0.3); opacity: 0; } 60% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
      @keyframes mcRowGlow { 0%,100% { border-color: #bfdbfe; } 50% { border-color: #3b82f6; } }
      @keyframes mcRing { from { stroke-dashoffset: var(--mc-ring-from); } to { stroke-dashoffset: var(--mc-ring-to); } }
    `}</style>
  );
}

export function SetupChecklist() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const setup = user?.setup;
  const uid = user?.id || user?.userId || user?.email || 'anon';
  const [open, setOpen] = useState(false);

  // `setup` is computed server-side only at login (authAPI.me). After the user adds a location or
  // courier in-session, that snapshot is stale, so the checklist keeps showing them as pending and
  // never disappears. Re-pull the live status: once on mount while still incomplete, and whenever
  // the modal is opened — me() recomputes setup each call, so completed steps tick off / it vanishes.
  const refreshedOnMount = React.useRef(false);
  React.useEffect(() => {
    if (!refreshedOnMount.current && setup && !setup.setup_complete) {
      refreshedOnMount.current = true;
      refreshUser();
    }
  }, [setup, refreshUser]);
  React.useEffect(() => {
    if (open) refreshUser();
  }, [open, refreshUser]);

  // Auto-open the guide the first time this browser sees it (until dismissed once).
  React.useEffect(() => {
    if (!setup || setup.setup_complete) return;
    try {
      if (!localStorage.getItem(`mc_setup_seen_${uid}`)) {
        setOpen(true);
        localStorage.setItem(`mc_setup_seen_${uid}`, '1');
      }
    } catch {}
  }, [uid, setup]);

  if (!user || user.approval_status !== 'approved' || !setup || setup.setup_complete) return null;

  const done = STEPS.filter(s => setup[s.key]).length;
  const nextStep = STEPS.find(s => !setup[s.key]);
  const go = (to) => { setOpen(false); navigate(to); };

  // Progress ring maths.
  const R = 26, C = 2 * Math.PI * R;
  const offset = C * (1 - done / STEPS.length);

  return (
    <>
      <GuideKeyframes />

      {/* Floating pill */}
      {!open && (
        <button style={g.pill} onClick={() => setOpen(true)}>
          <span style={g.pillDot}>{done}/{STEPS.length}</span>
          <span style={g.pillText}>Finish setup</span>
        </button>
      )}

      {/* Modal wizard */}
      {open && (
        <div style={g.overlay} onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div style={g.guideModal}>
            <button style={g.gClose} onClick={() => setOpen(false)}>✕</button>

            <div style={g.gHead}>
              <div style={g.ringWrap}>
                <svg width="64" height="64" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r={R} fill="none" stroke="#e2e8f0" strokeWidth="6" />
                  <circle cx="32" cy="32" r={R} fill="none" stroke="#2563eb" strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={C}
                    style={{ '--mc-ring-from': C, '--mc-ring-to': offset, strokeDashoffset: offset, animation: 'mcRing 0.8s ease forwards', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }} />
                  <text x="32" y="37" textAnchor="middle" style={g.ringText}>{done}/{STEPS.length}</text>
                </svg>
              </div>
              <div>
                <h2 style={g.gTitle}>{done === 0 ? "Let's get you shipping" : done === STEPS.length - 1 ? 'Almost there!' : 'Nice progress!'}</h2>
                <p style={g.gSub}>Finish these {STEPS.length} steps to unlock order shipping. You can explore the rest of the dashboard meanwhile.</p>
              </div>
            </div>

            <div style={g.gSteps}>
              {STEPS.map((s, i) => {
                const isDone = !!setup[s.key];
                const isNext = !isDone && nextStep && s.key === nextStep.key;
                return (
                  <button key={s.key}
                    style={{ ...g.gRow, ...(isDone ? g.gRowDone : {}), ...(isNext ? g.gRowNext : {}) }}
                    onClick={() => go(s.to)}>
                    <span style={{ ...g.gTick, ...(isDone ? g.gTickOn : {}) }}>
                      {isDone ? <span style={{ animation: 'mcTickPop 0.35s ease' }}>✓</span> : <span style={g.gEmoji}>{s.Ico ? <s.Ico size={16} /> : null}</span>}
                    </span>
                    <span style={g.gRowBody}>
                      <span style={g.gRowLabel}>{s.label}</span>
                      <span style={g.gRowDesc}>{s.desc}</span>
                    </span>
                    <span style={{ ...g.gRowCta, ...(isDone ? g.gRowCtaDone : {}) }}>{isDone ? 'Done' : (isNext ? 'Start →' : 'Set up →')}</span>
                  </button>
                );
              })}
            </div>

            {nextStep
              ? <button style={g.gPrimary} onClick={() => go(nextStep.to)}>Continue → {nextStep.label}</button>
              : <button style={g.gPrimary} onClick={() => setOpen(false)}>All done</button>}
          </div>
        </div>
      )}
    </>
  );
}

const g = {
  modal: { background: '#fff', borderRadius: 20, padding: '34px 30px', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: 'var(--mc-e4)' },
  wTitle: { color: '#0f172a', fontSize: 24, fontWeight: 800, margin: '10px 0 8px', letterSpacing: '-0.02em' },
  wSub: { color: '#64748b', fontSize: 14.5, lineHeight: 1.6, margin: '0 0 22px' },
  wBtn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },

  remBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'linear-gradient(90deg,#1e3a8a,#2563eb)', color: '#fff', padding: '9px 20px', borderBottom: '1px solid rgba(255,255,255,0.12)', flexWrap: 'wrap', flexShrink: 0 },
  remText: { fontSize: 13.5 },
  remBtn: { background: '#fff', color: '#1e40af', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  remClose: { background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 11px', fontSize: 13, cursor: 'pointer' },

  // Floating pill
  pill: { position: 'fixed', right: 22, bottom: 22, zIndex: 3400, display: 'flex', alignItems: 'center', gap: 9, background: 'linear-gradient(90deg,#1d4ed8,#2563eb)', color: '#fff', border: 'none', borderRadius: 999, padding: '11px 18px 11px 12px', cursor: 'pointer', fontSize: 14, fontWeight: 700, animation: 'mcPillPulse 2.2s ease-in-out infinite' },
  pillDot: { background: 'rgba(255,255,255,0.22)', borderRadius: 999, padding: '3px 9px', fontSize: 12.5, fontWeight: 800, letterSpacing: 0.3 },
  pillText: { paddingRight: 4 },

  // Modal wizard
  overlay: { position: 'fixed', inset: 0, background: 'rgba(9,14,27,0.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3500, padding: 16, animation: 'mcOverlayIn 0.2s ease' },
  guideModal: { position: 'relative', background: '#fff', borderRadius: 20, padding: '28px 26px 22px', maxWidth: 460, width: '100%', boxShadow: 'var(--mc-e4)', animation: 'mcModalIn 0.28s cubic-bezier(0.16,1,0.3,1)' },
  gClose: { position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', color: '#94a3b8', fontSize: 15, cursor: 'pointer', lineHeight: 1 },
  gHead: { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 },
  ringWrap: { flexShrink: 0 },
  ringText: { fontSize: 15, fontWeight: 800, fill: '#0f172a' },
  gTitle: { color: '#0f172a', fontSize: 20, fontWeight: 800, margin: '0 0 4px', letterSpacing: '-0.02em' },
  gSub: { color: '#64748b', fontSize: 13, lineHeight: 1.55, margin: 0 },
  gSteps: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 },
  gRow: { display: 'flex', alignItems: 'center', gap: 13, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 13, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', transition: 'transform var(--mc-t-fast) var(--mc-ease), box-shadow var(--mc-t-fast) var(--mc-ease)' },
  gRowDone: { background: '#f0fdf4', borderColor: '#bbf7d0' },
  gRowNext: { background: '#eff6ff', animation: 'mcRowGlow 1.8s ease-in-out infinite' },
  gTick: { width: 34, height: 34, borderRadius: '50%', background: '#e2e8f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: '#fff', flexShrink: 0 },
  gTickOn: { background: '#16a34a' },
  gEmoji: { fontSize: 17 },
  gRowBody: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  gRowLabel: { fontSize: 14, fontWeight: 700, color: '#0f172a' },
  gRowDesc: { fontSize: 11.5, color: '#64748b', lineHeight: 1.4 },
  gRowCta: { fontSize: 12, color: '#2563eb', fontWeight: 700, flexShrink: 0 },
  gRowCtaDone: { color: '#16a34a' },
  gPrimary: { width: '100%', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 0', fontSize: 14.5, fontWeight: 700, cursor: 'pointer' },
};
